import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AmbientAudio from './AmbientAudio';
import VoiceGuide from './VoiceGuide';
import { useTranslation } from 'react-i18next';

const SpecificExercises = ({ onComplete }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const tr = (obj) => (typeof obj === 'string' ? obj : (obj?.[lang] ?? obj?.en ?? ''));

  const L = {
    header: { en: 'Featured Breathing & Wellness Exercises', es: 'Ejercicios destacados de respiración y bienestar' },
    headerSub: { en: 'Guided exercises with visual instructions to help you find calm and center yourself', es: 'Ejercicios guiados con instrucciones visuales para encontrar calma y centrarte' },
    duration: { en: 'Duration', es: 'Duración' },
    steps: { en: 'Step-by-Step Instructions', es: 'Instrucciones paso a paso' },
    benefits: { en: 'Benefits', es: 'Beneficios' },
    complete: { en: 'Complete Exercise', es: 'Completar ejercicio' },
    back: { en: 'Back to Exercises', es: 'Volver a ejercicios' },
    start: { en: 'Start Exercise', es: 'Comenzar ejercicio' },
    more: { en: 'more', es: 'más' },
  };

  const [activeExercise, setActiveExercise] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showVoiceGuide, setShowVoiceGuide] = useState(false);

  const specificExercises = {
    box_breathing: {
      title: { en: 'Box Breathing with Hand Tracing', es: 'Respiración cuadrada trazando la mano' },
      description: { en: 'A calming technique combining controlled breathing with gentle tactile stimulation', es: 'Una técnica calmante que combina respiración controlada con estímulo táctil suave' },
      icon: '🫴',
      color: 'from-blue-400 to-indigo-500',
      image: 'https://customer-assets.emergentagent.com/job_b4cb5416-860e-4eba-a39a-e0249fb9ed92/artifacts/3axemsvr_1_20250630_211045_0000.png',
      instructions: {
        en: ['Hold up one hand in front of you', 'Use the pointer finger of your other hand', 'Slowly trace up the thumb while inhaling', 'Trace down the thumb while exhaling', 'Continue with each finger: up on inhale, down on exhale', 'Complete all five fingers for one full cycle', 'Repeat 2-3 cycles for maximum benefit'],
        es: ['Levanta una mano frente a ti', 'Usa el índice de la otra mano', 'Traza lentamente el pulgar hacia arriba al inhalar', 'Traza el pulgar hacia abajo al exhalar', 'Sigue con cada dedo: sube al inhalar, baja al exhalar', 'Completa los cinco dedos para un ciclo', 'Repite 2-3 ciclos para máximo beneficio'],
      },
      benefits: { en: ['Reduces anxiety', 'Improves focus', 'Accessible anywhere', 'Combines breath and touch'], es: ['Reduce la ansiedad', 'Mejora el enfoque', 'Accesible en cualquier lugar', 'Combina respiración y tacto'] },
      duration: { en: '3-5 minutes', es: '3-5 minutos' },
      protective_factors: { emotional_awareness: 0.15, coping_skills: 0.2 },
    },
    silent_lion: {
      title: { en: 'Silent Lion Breath', es: 'Respiración de león silencioso' },
      description: { en: 'A powerful stress-release technique using silent expression and stretching', es: 'Una técnica poderosa para liberar el estrés con expresión silenciosa y estiramiento' },
      icon: '🦁',
      color: 'from-orange-400 to-red-500',
      image: 'https://customer-assets.emergentagent.com/job_b4cb5416-860e-4eba-a39a-e0249fb9ed92/artifacts/dt9yebi6_9_20250630_080722_0008.png',
      instructions: {
        en: ['Find a comfortable seated or standing position', 'Take a deep breath in through your nose', 'Open your mouth wide as if to roar', 'Stretch your hands and arms wide', "Exhale with a silent 'roar' - feel the release", 'Let your shoulders drop and relax', 'Repeat this process 3 times'],
        es: ['Busca una postura cómoda, sentado/a o de pie', 'Inhala profundo por la nariz', 'Abre la boca bien como para rugir', 'Estira las manos y los brazos', "Exhala con un 'rugido' silencioso — siente la liberación", 'Deja caer los hombros y relájate', 'Repite el proceso 3 veces'],
      },
      benefits: { en: ['Releases stress', 'Energizes body', 'Builds confidence', 'Expressive release'], es: ['Libera estrés', 'Energiza el cuerpo', 'Aumenta la confianza', 'Liberación expresiva'] },
      duration: { en: '2-3 minutes', es: '2-3 minutos' },
      protective_factors: { coping_skills: 0.25, purpose_confidence: 0.15 },
    },
    body_tap: {
      title: { en: "'I Am Here' Body Tap", es: "Toque corporal 'Estoy aquí'" },
      description: { en: 'A grounding technique using gentle tapping and positive affirmations', es: 'Una técnica de conexión usando golpecitos suaves y afirmaciones positivas' },
      icon: '🤲',
      color: 'from-sky-400 to-green-500',
      image: 'https://customer-assets.emergentagent.com/job_b4cb5416-860e-4eba-a39a-e0249fb9ed92/artifacts/m60hg461_10_20250630_080722_0009.png',
      instructions: {
        en: ['Start with gentle tapping on your shoulders', 'Move to tapping your arms from shoulder to wrist', 'Gently tap your chest area', 'Continue down to tap your legs', "With each tap, say silently or softly: 'I am here. I am okay'", 'Place one hand over your heart', 'Take one deep, calming breath'],
        es: ['Empieza con golpecitos suaves en los hombros', 'Sigue golpeando los brazos del hombro a la muñeca', 'Toca suavemente el pecho', 'Continúa bajando a las piernas', "Con cada toque, di en silencio o suave: 'Estoy aquí. Estoy bien'", 'Pon una mano sobre tu corazón', 'Toma una respiración profunda y calmada'],
      },
      benefits: { en: ['Promotes grounding', 'Reduces anxiety', 'Body awareness', 'Self-soothing'], es: ['Promueve conexión', 'Reduce ansiedad', 'Conciencia corporal', 'Auto-calma'] },
      duration: { en: '3-4 minutes', es: '3-4 minutos' },
      protective_factors: { emotional_awareness: 0.2, coping_skills: 0.1, purpose_confidence: 0.1 },
    },
    chair_twist: {
      title: { en: 'Chair Twist Release', es: 'Liberación con giro en silla' },
      description: { en: 'A gentle seated exercise combining mindful finger tracing with synchronized breathing', es: 'Un ejercicio sentado suave que combina trazado consciente de los dedos con respiración sincronizada' },
      icon: '💺',
      color: 'from-purple-400 to-pink-500',
      image: 'https://customer-assets.emergentagent.com/job_b4cb5416-860e-4eba-a39a-e0249fb9ed92/artifacts/ikgl7ny9_8_20250630_211045_0007.png',
      instructions: {
        en: ['Sit comfortably in a chair', 'Hold up one hand in front of you', 'Use the pointer finger of your other hand', 'Slowly trace up and down each finger', 'Inhale while tracing up each finger', 'Exhale while tracing down each finger', 'Complete all five fingers for full relaxation'],
        es: ['Siéntate cómodo/a en una silla', 'Levanta una mano frente a ti', 'Usa el índice de la otra mano', 'Traza lentamente cada dedo arriba y abajo', 'Inhala al subir por cada dedo', 'Exhala al bajar por cada dedo', 'Completa los cinco dedos para relajación total'],
      },
      benefits: { en: ['Office-friendly', 'Reduces tension', 'Improves focus', 'Easy to learn'], es: ['Apto para la oficina', 'Reduce tensión', 'Mejora el enfoque', 'Fácil de aprender'] },
      duration: { en: '2-3 minutes', es: '2-3 minutos' },
      protective_factors: { emotional_awareness: 0.15, coping_skills: 0.15 },
    },
    tap_focus: {
      title: { en: 'Tap and Focus Reset', es: 'Reinicio con toque y enfoque' },
      description: { en: 'A rhythmic grounding technique using gentle tapping to restore focus and calm', es: 'Una técnica rítmica de conexión que usa golpecitos suaves para restaurar enfoque y calma' },
      icon: '👆',
      color: 'from-yellow-400 to-orange-500',
      image: 'https://customer-assets.emergentagent.com/job_b4cb5416-860e-4eba-a39a-e0249fb9ed92/artifacts/sd36j7q4_7_20250630_211045_0006.png',
      instructions: {
        en: ['Gently tap your fingers on the desk surface', 'Place your feet firmly on the floor', 'Start with a slow, gentle tapping tempo', 'Try switching to different tempos', 'Focus only on the physical feeling of tapping', 'Pay attention to the rhythm you create', 'Continue for 30 seconds'],
        es: ['Da golpecitos suaves con los dedos en la mesa', 'Apoya los pies firmes en el suelo', 'Comienza con un ritmo lento y suave', 'Cambia a distintos ritmos', 'Concéntrate solo en la sensación física', 'Presta atención al ritmo que creas', 'Continúa 30 segundos'],
      },
      benefits: { en: ['Quick reset', 'Improves concentration', 'Dual sensory engagement', 'Workplace appropriate'], es: ['Reinicio rápido', 'Mejora concentración', 'Estímulo doble sensorial', 'Apto para el trabajo'] },
      duration: { en: '30 seconds', es: '30 segundos' },
      protective_factors: { emotional_awareness: 0.1, coping_skills: 0.2 },
    },
    five_senses: {
      title: { en: 'Five Senses Grounding Check-In', es: 'Conexión con los cinco sentidos' },
      description: { en: 'A comprehensive mindfulness exercise engaging all five senses for present moment awareness', es: 'Un ejercicio integral de mindfulness que involucra los cinco sentidos para la conciencia del presente' },
      icon: '🌟',
      color: 'from-green-400 to-amber-500',
      image: 'https://customer-assets.emergentagent.com/job_b4cb5416-860e-4eba-a39a-e0249fb9ed92/artifacts/v7856rbs_2_20250630_211045_0001.png',
      instructions: {
        en: ['Find a comfortable position (can be done silently or aloud)', 'Name 5 things you can see around you', 'Identify 4 things you can physically touch', 'Notice 3 things you can hear right now', 'Recognize 2 things you can smell', 'Think of 1 thing you can taste or remember tasting', 'Take a moment to appreciate this present moment'],
        es: ['Busca una postura cómoda (en silencio o en voz alta)', 'Nombra 5 cosas que puedes ver', 'Identifica 4 cosas que puedes tocar', 'Nota 3 cosas que puedes oír', 'Reconoce 2 cosas que puedes oler', 'Piensa en 1 cosa que puedes saborear o recuerdes haber saboreado', 'Tómate un momento para apreciar el presente'],
      },
      benefits: { en: ['Complete grounding', 'Anxiety relief', 'Present moment awareness', 'Versatile technique'], es: ['Conexión completa', 'Alivio de ansiedad', 'Conciencia del presente', 'Técnica versátil'] },
      duration: { en: '3-5 minutes', es: '3-5 minutos' },
      protective_factors: { emotional_awareness: 0.25, coping_skills: 0.15, problem_solving: 0.1 },
    },
  };

  const startExercise = (key) => {
    setActiveExercise(key); setAudioPlaying(true); setShowVoiceGuide(true);
  };

  const completeExercise = () => {
    const ex = specificExercises[activeExercise];
    onComplete({
      activity_type: activeExercise,
      title: tr(ex.title),
      protective_factors_gained: ex.protective_factors,
      notes: `Completed ${tr(ex.title)} - ${tr(ex.description)}`,
    });
    setActiveExercise(null); setAudioPlaying(false); setShowVoiceGuide(false);
  };

  if (activeExercise) {
    const ex = specificExercises[activeExercise];
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid={`active-exercise-${activeExercise}`}>
        <Card className="glass-card p-8">
          <div className="text-center mb-8">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r ${ex.color} flex items-center justify-center text-4xl`}>
              {ex.icon}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{tr(ex.title)}</h1>
            <p className="text-gray-600 text-lg mb-4">{tr(ex.description)}</p>
            <Badge className="bg-blue-100 text-blue-700">{tr(L.duration)}: {tr(ex.duration)}</Badge>
          </div>

          <VoiceGuide
            exercise={{ type: activeExercise, ...ex, title: tr(ex.title), description: tr(ex.description), instructions: tr(ex.instructions) }}
            isActive={showVoiceGuide}
            onComplete={() => setShowVoiceGuide(false)}
          />

          <div className="mb-8">
            <img src={ex.image} alt={tr(ex.title)} className="w-full max-w-md mx-auto rounded-lg shadow-lg" />
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{tr(L.steps)}</h2>
            <div className="space-y-3">
              {tr(ex.instructions).map((instruction, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">{i + 1}</div>
                  <p className="text-gray-700 leading-relaxed">{instruction}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{tr(L.benefits)}</h2>
            <div className="flex flex-wrap gap-2">
              {tr(ex.benefits).map((b, i) => (
                <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700">{b}</Badge>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <AmbientAudio isPlaying={audioPlaying} onToggle={() => setAudioPlaying(!audioPlaying)} />
          </div>

          <div className="flex space-x-4">
            <Button onClick={completeExercise} className="btn-primary flex-1" data-testid={`complete-${activeExercise}-btn`}>
              {tr(L.complete)} ✨
            </Button>
            <Button
              onClick={() => { setActiveExercise(null); setAudioPlaying(false); setShowVoiceGuide(false); }}
              variant="outline" className="btn-secondary" data-testid={`back-${activeExercise}-btn`}
            >
              {tr(L.back)}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{tr(L.header)}</h2>
        <p className="text-gray-600">{tr(L.headerSub)}</p>
      </div>

      <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
        {Object.entries(specificExercises).map(([key, ex]) => (
          <Card key={key} className="activity-card group" data-testid={`exercise-card-${key}`}>
            <div className="relative overflow-hidden rounded-t-lg mb-4">
              <img src={ex.image} alt={tr(ex.title)} className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute top-2 right-2">
                <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${ex.color} flex items-center justify-center text-lg`}>{ex.icon}</div>
              </div>
            </div>
            <div className="px-4 pb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{tr(ex.title)}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{tr(ex.description)}</p>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-gray-500">{tr(L.duration)}:</span>
                  <Badge variant="outline" className="text-xs">{tr(ex.duration)}</Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {tr(ex.benefits).slice(0, 2).map((b, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{b}</Badge>
                  ))}
                  {tr(ex.benefits).length > 2 && (
                    <Badge variant="outline" className="text-xs text-gray-400">+{tr(ex.benefits).length - 2} {tr(L.more)}</Badge>
                  )}
                </div>
              </div>
              <Button onClick={() => startExercise(key)} className="btn-primary w-full" data-testid={`start-${key}-btn`}>
                {tr(L.start)}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SpecificExercises;
